import React, { useMemo, useState } from 'react';
import { Modal, Button, Form, Table } from 'react-bootstrap';
import API from '../services/api';
import { useAuth } from '../context/AuthContext';

const money = (n) => new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP' }).format(n || 0);

// Issues a credit note (E34) against an invoice DGII already accepted: creates
// it, then sends it straight to DGII. If sending fails the note stays in
// "Lista" and can be sent later from the Facturas list.
const CreditNoteModal = ({ invoice, onClose, onChanged }) => {
  const { user } = useAuth();
  const asking = user?.role !== 'admin'; // a sales user asks an admin instead of issuing the note
  const alreadyCredited = invoice.creditedAmount || 0;
  const [code, setCode] = useState(alreadyCredited > 0 ? 3 : 1);
  const [reason, setReason] = useState('');
  const [lines, setLines] = useState(() =>
    invoice.items.map(it => ({ quantity: String(it.quantity), unitPrice: String(it.unitPrice) }))
  );
  const [texts, setTexts] = useState(() => invoice.items.map(it => it.description));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [createdNote, setCreatedNote] = useState(null);

  const changedTexts = texts
    .map((t, i) => ({ lineIndex: i, description: t.trim() }))
    .filter(l => l.description && l.description !== invoice.items[l.lineIndex].description);

  const creditTotal = useMemo(() => code === 2 ? 0 : invoice.items.reduce((sum, it, i) => {
    const q = code === 1 ? it.quantity : Number(lines[i].quantity) || 0;
    const p = code === 1 ? it.unitPrice : Number(lines[i].unitPrice) || 0;
    const line = Math.max(q, 0) * Math.max(p, 0);
    return sum + line + (it.itbisExento ? 0 : line * (it.itbisRate ?? 0.18));
  }, 0), [invoice, code, lines]);

  const remaining = invoice.total - alreadyCredited;
  const setLine = (i, field, value) => setLines(prev => prev.map((l, idx) => (idx === i ? { ...l, [field]: value } : l)));

  const submit = async () => {
    setBusy(true);
    setError('');
    let note = createdNote;
    try {
      if (!note) {
        const body = { modificationCode: code, reason };
        if (code === 2) {
          body.lines = changedTexts;
        } else if (code === 3) {
          body.lines = lines
            .map((l, i) => ({ lineIndex: i, quantity: Number(l.quantity), unitPrice: Number(l.unitPrice) }))
            .filter(l => l.quantity > 0);
        }
        const created = await API.post(`/invoices/${invoice._id}/credit-note`, body);
        if (created.status === 202) { onChanged(); onClose(); return; } // asked of the admins (the toast says so)
        note = created.data;
        setCreatedNote(note);
      }
      const { data } = await API.post(`/invoices/${note._id}/send-ecf`);
      onChanged();
      if (data.dgiiOutcome === 'rejected') {
        setError(`DGII rechazó la nota de crédito: ${data.invoice?.ecf?.dgiiMessage || 'sin detalle'}`);
        return;
      }
      onClose();
    } catch (err) {
      const message = err.response?.data?.message || 'Error al emitir la nota de crédito.';
      setError(note ? `La nota de crédito ${note.number} se creó, pero no se pudo enviar a DGII: ${message}` : message);
      if (note) onChanged();
    } finally {
      setBusy(false);
    }
  };

  const sendOnly = createdNote && !error.startsWith('DGII rechazó');

  return (
    <Modal show onHide={busy ? undefined : onClose} size="lg" centered>
      <Modal.Header closeButton={!busy}>
        <Modal.Title>Nota de crédito — {invoice.number}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="small text-muted mb-3">
          Modifica {invoice.ncf} · {invoice.client?.name} · Total {money(invoice.total)}
          {alreadyCredited > 0 && <> · Ya acreditado {money(alreadyCredited)} · Pendiente {money(remaining)}</>}
        </div>

        {error && <div className="alert alert-danger">{error}</div>}
        {asking && (
          <div className="alert alert-note small">
            Una nota necesita la aprobación de un administrador. La pides aquí y sigues con tu trabajo: cuando la apruebe, la nota se crea
            y se envía a DGII sola (verás el resultado en Aprobaciones).
          </div>
        )}


        <Form.Group className="mb-3">
          <Form.Label>Tipo de nota de crédito</Form.Label>
          <Form.Check
            type="radio" id="cn-code-1" name="cn-code" disabled={!!createdNote || alreadyCredited > 0}
            label={alreadyCredited > 0 ? 'Anular la factura completa (no disponible: ya tiene notas de crédito)' : 'Anular la factura completa'}
            checked={code === 1} onChange={() => setCode(1)}
          />
          <Form.Check
            type="radio" id="cn-code-3" name="cn-code" disabled={!!createdNote}
            label="Corregir montos (devolución o diferencia de precio en algunas líneas)"
            checked={code === 3} onChange={() => setCode(3)}
          />
          <Form.Check
            type="radio" id="cn-code-2" name="cn-code" disabled={!!createdNote}
            label="Corregir texto (descripción mal escrita; sin monto)"
            checked={code === 2} onChange={() => setCode(2)}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Motivo <span className="text-muted small">(máx. 90 caracteres, va en el comprobante)</span></Form.Label>
          <Form.Control
            value={reason} maxLength={90} disabled={!!createdNote}
            onChange={e => setReason(e.target.value)} placeholder="Ej.: devolución de equipo defectuoso"
          />
        </Form.Group>

        {code === 2 ? (
          <Table size="sm" className="mb-2">
            <thead>
              <tr><th style={{ width: '40%' }}>Texto original</th><th>Texto corregido (máx. 80 caracteres)</th></tr>
            </thead>
            <tbody>
              {invoice.items.map((it, i) => (
                <tr key={i}>
                  <td>{it.description}</td>
                  <td>
                    <Form.Control size="sm" maxLength={80} disabled={!!createdNote}
                      value={texts[i]} onChange={e => setTexts(prev => prev.map((t, idx) => (idx === i ? e.target.value : t)))} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <Table size="sm" className="mb-2">
            <thead>
              <tr><th>Descripción</th><th className="text-end">Cant. original</th><th style={{ width: 110 }}>Cant. a acreditar</th><th style={{ width: 130 }}>Precio a acreditar</th></tr>
            </thead>
            <tbody>
              {invoice.items.map((it, i) => (
                <tr key={i}>
                  <td>{it.description}</td>
                  <td className="text-end">{it.quantity} × {money(it.unitPrice)}</td>
                  <td>
                    <Form.Control size="sm" type="number" min="0" max={it.quantity} step="1"
                      disabled={code === 1 || !!createdNote}
                      value={code === 1 ? it.quantity : lines[i].quantity}
                      onChange={e => setLine(i, 'quantity', e.target.value)} />
                  </td>
                  <td>
                    <Form.Control size="sm" type="number" min="0" max={it.unitPrice} step="0.01"
                      disabled={code === 1 || !!createdNote}
                      value={code === 1 ? it.unitPrice : lines[i].unitPrice}
                      onChange={e => setLine(i, 'unitPrice', e.target.value)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        <div className="text-end">
          {code === 2
            ? <>Sin monto: solo corrige el texto de la factura.</>
            : <>Total a acreditar (con ITBIS): <strong>{money(creditTotal)}</strong></>}
        </div>
        <div className="small text-muted mt-2">
          {code === 2
            ? 'El nombre del cliente se toma de su ficha actual: si lo corregiste en Clientes, la nota lo llevará corregido. El RNC no puede cambiar.'
            : 'Para una diferencia de precio, deja la cantidad y baja el precio al monto que se acredita por unidad.'}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose} disabled={busy}>{createdNote ? 'Cerrar' : 'Cancelar'}</Button>
        {!(createdNote && !sendOnly) && (
          <Button onClick={submit} disabled={busy || !reason.trim() || (code === 2 ? changedTexts.length === 0 : creditTotal <= 0)}>
            {busy ? 'Enviando...' : sendOnly ? 'Reintentar envío a DGII' : asking ? 'Pedir aprobación' : 'Emitir y enviar a DGII'}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default CreditNoteModal;
